import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import { FandomTaxonomyPlugin } from "../FandomTaxonomyPlugin/FandomTaxonomyPlugin";
import { PrebidPlugin } from "../PrebidPlugin/PrebidPlugin";
import type { OpenRtb2Object } from "./openRtb";
import type { TaxonomyTags } from "common-types";
import type { PageTargeting } from "../../config/page";
import { IAB_CATTAX } from "../FandomTaxonomyPlugin/consts";
import type { AdEngine } from "../../core/AdEngine";

interface OpenRtbPluginApi {
  /**
   * Builds and returns an OpenRTB object based on PageTargeting values and the current page
   * contexts.
   *
   * @param pageTargetingOverrides Overrides for page targeting. These will be applied on top of the
   * current page targeting retrieved from the gptWrapper.getAllTargeting() method.
   * @returns The build OpenRTB object.
   */
  getOrtb2: (pageTargetingOverrides?: PageTargeting) => OpenRtb2Object;
}

export class OpenRtbPlugin extends AdEnginePlugin<OpenRtbPluginApi> {
  /**
   * 6 = Taxonomy 2.2
   * @see https://github.com/InteractiveAdvertisingBureau/AdCOM/blob/main/AdCOM%20v1.0%20FINAL.md#list_categorytaxonomies
   */
  private pageTargeting: PageTargeting = {};
  private adEngine?: AdEngine;
  private readonly dsaSettings = {
    dsa: {
      dsarequired: 1,
      pubrender: 2,
      datatopub: 2,
    },
  };
  private orbt2SiteCategories: string[] = [];
  private ortb2PageCategories: string[] = [];

  private getKeywordsSet(tags: TaxonomyTags): Set<string> {
    return new Set([
      ...(tags.gnre ?? []),
      ...(tags.media ?? []),
      ...(tags.theme ?? []),
      ...(tags.tv ?? []),
    ]);
  }

  private buildKeywords(): string {
    const siteContext = window?.fandomContext?.site;

    if (!siteContext?.tags) {
      return "";
    }

    return Array.from(this.getKeywordsSet(siteContext.tags).values())
      .filter(Boolean)
      .join(",");
  }

  private setOrtb2Categories(): void {
    const fandomTaxonomyPlugin = this.usePlugin(FandomTaxonomyPlugin);
    this.orbt2SiteCategories =
      fandomTaxonomyPlugin?.getIABSiteCategories() ?? [];
    this.ortb2PageCategories =
      fandomTaxonomyPlugin?.getIABPageCategories() ?? [];
  }

  private getOrtb2(): OpenRtb2Object {
    this.setOrtb2Categories();
    return {
      site: {
        cattax: IAB_CATTAX,
        cat: [...this.orbt2SiteCategories],
        pagecat: [...this.ortb2PageCategories],
        id: this.pageTargeting.pageId?.toString(),
        name: this.pageTargeting.pageName,
        content: {
          language: this.pageTargeting.lang,
          context: 5,
        },
        keywords: this.buildKeywords(),
        domain: window.location.hostname,
      },
      regs: {
        ext: {
          ...this.dsaSettings,
        },
      },
    };
  }

  private extendPrebidConfig(pbjs: Prebid) {
    pbjs.mergeConfig({
      ortb2: this.getOrtb2(),
    });
  }

  hooks: Hooks = {
    onAdEngineInit: (adEngine: AdEngine) => {
      this.adEngine = adEngine;

      const prebidPlugin = this.usePlugin(PrebidPlugin);
      prebidPlugin?.addPrebidReadyCallback((pbjs: Prebid) => {
        this.pageTargeting = { ...adEngine.gptWrapper.getAllTargeting() };
        this.extendPrebidConfig(pbjs);
      });
    },
  };

  api: OpenRtbPluginApi = {
    getOrtb2: (pageTargetingOverrides: PageTargeting = {}): OpenRtb2Object => {
      const pageTargeting = this.adEngine?.gptWrapper.getAllTargeting() ?? {};
      this.pageTargeting = { ...pageTargeting, ...pageTargetingOverrides };
      return this.getOrtb2();
    },
  };
}
